<template>
  <div>
    <div v-if="isDraftLoading" class="d-flex align-center justify-center" style="height: 100vh">
      <v-progress-circular color="primary" indeterminate size="64" />
    </div>

    <batch-form-fields
      v-else-if="batch !== undefined && draftData !== undefined"
      :batch="batch"
      :batch-sync-targets="draftData.syncTargets"
      :saving="saving"
      :isCreateMode="true"
      @save-clicked="handleSaveClicked"
    />

    <vs-snackbar v-model="isSnackbarVisible" :type="snackbarType" sticky>
      {{ snackbarMessage ?? '' }}
    </vs-snackbar>
  </div>
</template>

<script setup lang="ts">
import moment from 'moment'
import { computed, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { BatchFormData, BatchPaymentTerms } from '@/models/batch'
import {
  CustomDataWithSchema,
  useAddJobsToInvoiceMutation,
  useCreateInvoiceMutation,
  useInvoiceDraftQuery,
} from '@/api/invoice'
import { AxiosError } from 'axios'
import { SnackType } from '@/components/vision/SnackBarType'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import BatchFormFields from '@/components/batches/BatchFormFields.vue'

const props = defineProps<{
  batchName: string
  jobsUuidList: string[]
}>()

const emit = defineEmits<{
  (e: 'batch-created-success', data: { batchId: string; batchNumber: string | null })
  (e: 'batch-created-failure', error: unknown)
}>()

// ── State ──────────────────────────────────────────────────────────────────

const isSnackbarVisible = ref(false)
const snackbarMessage = ref<string | null>(null)
const snackbarType = ref(SnackType.ERROR)

// ── Data fetching ──────────────────────────────────────────────────────────

const batchNameRef = computed(() => props.batchName)
const jobIdsRef = computed(() => props.jobsUuidList)
const { data: draftData, isLoading: isDraftLoading } = useInvoiceDraftQuery(batchNameRef, jobIdsRef)

const store = useStore()
const organizationId = computed<string>(() => store.getters['permissions/getOrganizationId'])

// ── Mutations ──────────────────────────────────────────────────────────────

const { mutateAsync: createBatch, isLoading: isCreating } = useCreateInvoiceMutation()

const { mutateAsync: addJobsToBatch, isLoading: isAddingJobs } = useAddJobsToInvoiceMutation()

const saving = computed(() => isCreating.value || isAddingJobs.value)

// ── Batch computed ─────────────────────────────────────────────────────────

const batch = computed<BatchFormData | undefined>(() => {
  if (!draftData.value) return undefined
  return {
    id: '',
    invoiceNumber: draftData.value.batchName,
    invoiceDate: moment.utc(draftData.value.batchDate).local(),
    paymentTerms: draftData.value.paymentTerms,
    dueDate: moment.utc(draftData.value.dueDate).local(),
    memo: draftData.value.memo,
  }
})

// ── Save ───────────────────────────────────────────────────────────────────

async function handleSaveClicked(payload: {
  id: string
  customDataPerApp: Record<string, CustomDataWithSchema>
  invoiceNumber: string | null | undefined
  invoiceDate: moment.Moment | undefined
  paymentTerms: BatchPaymentTerms | null
  dueDate: moment.Moment | undefined
  jobIds: undefined
  memo: string | null | undefined
}) {
  try {
    const response = await createBatch({
      id: null,
      invoiceNumber: payload.invoiceNumber ?? null,
      invoiceDate: payload.invoiceDate ?? moment().utc(),
      paymentTerms: payload.paymentTerms ?? null,
      dueDate: payload.dueDate ?? null,
      jobIds: props.jobsUuidList,
      ownerId: organizationId.value,
      memo: payload.memo ?? null,
      customDataPerApp: payload.customDataPerApp,
    })

    const newBatchId = response.data.id as string

    emit('batch-created-success', {
      batchId: newBatchId,
      batchNumber: payload.invoiceNumber ?? null,
    })
  } catch (e) {
    const axiosErr = e as AxiosError | null
    if (axiosErr?.response?.status === 409) {
      snackbarMessage.value = `Batch number "${payload.invoiceNumber}" already exists`
    } else {
      snackbarMessage.value = 'Failed to create batch'
    }
    snackbarType.value = SnackType.ERROR
    isSnackbarVisible.value = true
    emit('batch-created-failure', e)
  }
}
</script>
